import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  amenitiesCategories,
  safetyCategories,
  rulesCategories,
  EntertainmentAmenities,
  ChildrenAmenities,
  KitchenAmenities,
  LaundryAmenities,
  BedroomAmenities,
  BathroomAmenities,
  OfficeAmenities,
  CategoryItem,
} from "../../../../CATEGORIES/Categories";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  listenToShortstayData,
  upsertShortstay,
} from "../../../../redux/shortstaysSlice";

// 🖼️ IMAGE TOGGLE — controls whether amenity images render instead of icons.
// To SHOW images: flip this to true.
const SHOW_AMENITY_IMAGES = false;

const EditShortAmenities: React.FC = () => {
  const { listingId } = useParams<{ listingId: string }>();
  const dispatch = useDispatch<AppDispatch>();

  const currentListing = useSelector(
    (state: RootState) => state.shortstays.currentListing
  );

  const [amenities, setAmenities] = useState<number[]>([]);
  const [safety, setSafety] = useState<number[]>([]);
  const [rules, setRules] = useState<number[]>([]);
  const [kitchenAmenities, setKitchenAmenities] = useState<number[]>([]);
  const [laundryAmenities, setLaundryAmenities] = useState<number[]>([]);
  const [bedroomAmenities, setBedroomAmenities] = useState<number[]>([]);
  const [bathroomAmenities, setBathroomAmenities] = useState<number[]>([]);
  const [entertainmentAmenities, setEntertainmentAmenities] = useState<number[]>([]);
  const [childrenAmenities, setChildrenAmenities] = useState<number[]>([]);
  const [officeAmenities, setOfficeAmenities] = useState<number[]>([]);

  const [isUpdating, setIsUpdating] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // 🔹 Load listing
  useEffect(() => {
    if (listingId) {
      dispatch(listenToShortstayData(listingId));
    }
  }, [dispatch, listingId]);

  // 🔹 Populate form when listing loads
  useEffect(() => {
    if (!currentListing) return;

    setAmenities(currentListing.amenities ?? []);
    setSafety(currentListing.safety ?? []);
    setRules(currentListing.rules ?? []);
    setKitchenAmenities(currentListing.kitchenAmenities ?? []);
    setLaundryAmenities(currentListing.laundryAmenities ?? []);
    setBedroomAmenities(currentListing.bedroomAmenities ?? []);
    setBathroomAmenities(currentListing.bathroomAmenities ?? []);
    setEntertainmentAmenities(currentListing.entertainmentAmenities ?? []);
    setChildrenAmenities(currentListing.childrenAmenities ?? []);
    setOfficeAmenities(currentListing.officeAmenities ?? []);
    setHasChanges(false);
  }, [currentListing]);

  // 🔹 Detect changes
  const checkChanges = useCallback(() => {
    if (!currentListing) return false;

    const fieldsMatch = (key: string, value: number[]) =>
      JSON.stringify(currentListing[key] ?? []) === JSON.stringify(value);

    return !(
      fieldsMatch("amenities", amenities) &&
      fieldsMatch("safety", safety) &&
      fieldsMatch("rules", rules) &&
      fieldsMatch("kitchenAmenities", kitchenAmenities) &&
      fieldsMatch("laundryAmenities", laundryAmenities) &&
      fieldsMatch("bedroomAmenities", bedroomAmenities) &&
      fieldsMatch("bathroomAmenities", bathroomAmenities) &&
      fieldsMatch("entertainmentAmenities", entertainmentAmenities) &&
      fieldsMatch("childrenAmenities", childrenAmenities) &&
      fieldsMatch("officeAmenities", officeAmenities)
    );
  }, [
    amenities,
    safety,
    rules,
    kitchenAmenities,
    laundryAmenities,
    bedroomAmenities,
    bathroomAmenities,
    entertainmentAmenities,
    childrenAmenities,
    officeAmenities,
    currentListing,
  ]);

  useEffect(() => {
    setHasChanges(checkChanges());
  }, [checkChanges]);

  // 🔹 Toggle selection
  const toggleSelection = (
    id: number,
    setArray: React.Dispatch<React.SetStateAction<number[]>>
  ) => {
    if (isUpdating) return;

    setArray((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // 🔹 Save updates
  const handleSave = async () => {
    if (!listingId || !hasChanges) return;

    setIsUpdating(true);

    try {
      await dispatch(
        upsertShortstay({
          listingId,
          data: {
            amenities,
            safety,
            rules,
            kitchenAmenities,
            laundryAmenities,
            bedroomAmenities,
            bathroomAmenities,
            entertainmentAmenities,
            childrenAmenities,
            officeAmenities,
          },
        })
      );

      alert("✅ Amenities updated successfully!");
      setHasChanges(false);
    } catch (err) {
      console.error(err);
      alert("❌ Update failed. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  const renderIcon = (item: CategoryItem) => (
    SHOW_AMENITY_IMAGES && item.image ? (
      <img
        src={item.image}
        alt={item.name}
        width={32}
        height={32}
        style={{ objectFit: "contain" }}
      />
    ) : (
      item.icon
    )
  );

  const renderSection = (
    title: string,
    items: CategoryItem[],
    selected: number[],
    setSelected: React.Dispatch<React.SetStateAction<number[]>>
  ) => (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>{title}</h3>

      <div style={styles.grid}>
        {items.map((item) => {
          const isSelected = selected.includes(item.id);

          return (
            <div
              key={`${title}-${item.id}`}
              onClick={() => toggleSelection(item.id, setSelected)}
              style={{
                ...styles.card,
                background: isSelected
                  ? "linear-gradient(to right, #111, #bf9b30)"
                  : "#111",
                border: isSelected ? "2px solid #bf9b30" : "1px solid #333",
                color: isSelected ? "#fff" : "#ccc",
                cursor: isUpdating ? "not-allowed" : "pointer",
                opacity: isUpdating ? 0.6 : 1,
              }}
              role="button"
              aria-pressed={isSelected}
            >
              <div style={styles.icon}>{renderIcon(item)}</div>
              <p>{item.name}</p>
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={styles.wrapper}>
      <div style={styles.header}>
        <h2 style={styles.title}>Shortstay Amenities & Details</h2>

        <button
          onClick={handleSave}
          disabled={!hasChanges || isUpdating}
          style={{
            ...styles.saveButton,
            opacity: !hasChanges || isUpdating ? 0.4 : 1,
            cursor: !hasChanges || isUpdating ? "not-allowed" : "pointer",
          }}
        >
          {isUpdating ? "Saving..." : "Save"}
        </button>
      </div>

      {renderSection("Amenities", amenitiesCategories, amenities, setAmenities)}
      {renderSection("Kitchen", KitchenAmenities, kitchenAmenities, setKitchenAmenities)}
      {renderSection("Laundry", LaundryAmenities, laundryAmenities, setLaundryAmenities)}
      {renderSection("Bedroom", BedroomAmenities, bedroomAmenities, setBedroomAmenities)}
      {renderSection("Bathroom", BathroomAmenities, bathroomAmenities, setBathroomAmenities)}
      {renderSection("Entertainment", EntertainmentAmenities, entertainmentAmenities, setEntertainmentAmenities)}
      {renderSection("Family & Children", ChildrenAmenities, childrenAmenities, setChildrenAmenities)}
      {renderSection("Office", OfficeAmenities, officeAmenities, setOfficeAmenities)}
      {renderSection("Safety", safetyCategories, safety, setSafety)}
      {renderSection("Rules", rulesCategories, rules, setRules)}
    </div>
  );
};

export default EditShortAmenities;

const styles: { [key: string]: React.CSSProperties } = {
  wrapper: {
    padding: 24,
  },

  header: {
    marginBottom: 20,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
  },

  title: {
    fontSize: 22,
    fontWeight: "bold",
  },

  saveButton: {
    background: "linear-gradient(to right, #fff, #bf9b30)",
    border: "none",
    borderRadius: 30,
    color: "#000",
    fontWeight: "bold",
    padding: "10px 24px",
    fontSize: 16,
  },

  section: {
    marginBottom: 40,
  },

  sectionTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 12,
  },

  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 16,
  },

  card: {
    padding: 16,
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    transition: "all 0.3s ease",
    background: "#111",
    boxShadow: "0 0 12px rgba(255,255,255,0.05)",
    userSelect: "none",
  },

  icon: {
    fontSize: 32,
    marginBottom: 8,
    display: "flex",
    justifyContent: "center",
  },
};